import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Upload, X } from 'lucide-react';
import { getAllPressReleases, updatePressRelease } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SimpleEditor from '@/components/SimpleEditor';
import { convertToHtml, convertFromHtml } from '@/lib/textUtils';
import { toast } from 'sonner';

const EditPressRelease = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  useEffect(() => {
    const fetchPressRelease = async () => {
      try {
        const response = await getAllPressReleases();
        const pr = response.data.find(p => p.id === id);
        if (pr) {
          // Convert HTML content back to markdown-like text for editing
          const editableContent = convertFromHtml(pr.content);
          setFormData({
            title: pr.title,
            slug: pr.slug,
            company: pr.company,
            location: pr.location,
            date: pr.date.split('T')[0],
            featuredImage: pr.featuredImage || '',
            content: editableContent,
            excerpt: pr.excerpt || '',
            categories: pr.categories.join(', '),
            tags: pr.tags.join(', '),
            author: pr.author,
            status: pr.status
          });
          if (pr.featuredImage) {
            setImagePreview(pr.featuredImage);
          }
        } else {
          toast.error('Press release not found');
          navigate('/admin/dashboard');
        }
      } catch (error) {
        console.error('Error fetching press release:', error);
        toast.error('Failed to load press release');
        navigate('/admin/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchPressRelease();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-generate excerpt from content
      if (name === 'content') {
        updated.excerpt = value.substring(0, 200);
      }
      return updated;
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData(prev => ({ ...prev, featuredImage: base64String }));
        setImagePreview(base64String);
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, featuredImage: '' }));
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        // Convert markdown-like content to HTML
        content: convertToHtml(formData.content),
        categories: formData.categories.split(',').map(c => c.trim()).filter(Boolean),
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      await updatePressRelease(id, data);
      toast.success('Press release updated successfully!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error updating press release:', error);
      const message = error.response?.data?.detail || 'Failed to update press release';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  return (
    <div data-testid="edit-pr-page">
      <div className="bg-white rounded-lg shadow-sm p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit Press Release</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <Input
              id="title"
              name="title"
              type="text"
              data-testid="edit-title-input"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter press release title"
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
              Slug * (URL-friendly identifier)
            </label>
            <Input
              id="slug"
              name="slug"
              type="text"
              data-testid="edit-slug-input"
              value={formData.slug}
              onChange={handleChange}
              required
              placeholder="url-friendly-slug"
            />
          </div>

          {/* Company and Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                Company *
              </label>
              <Input
                id="company"
                name="company"
                type="text"
                data-testid="edit-company-input"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="Company name"
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <Input
                id="location"
                name="location"
                type="text"
                data-testid="edit-location-input"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="City, State/Country"
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <Input
              id="date"
              name="date"
              type="date"
              data-testid="edit-date-input"
              value={formData.date}
              onChange={handleChange}
              required
              className="max-w-xs"
            />
          </div>

          {/* Featured Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            
            {!imagePreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-navy-400 transition-colors">
                <input
                  type="file"
                  id="image-upload-edit"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="edit-image-upload"
                />
                <label
                  htmlFor="image-upload-edit"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <span className="text-sm font-medium text-gray-700 mb-1">
                    Click to upload or drag and drop
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative border-2 border-gray-200 rounded-lg p-4">
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                  data-testid="remove-image-button-edit"
                >
                  <X size={16} />
                </button>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-64 mx-auto rounded-lg"
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  Current image (click X to replace)
                </p>
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <SimpleEditor
              value={formData.content}
              onChange={handleChange}
              placeholder="Enter your press release content here..."
              rows={16}
              dataTestId="edit-content-input"
            />
          </div>

          {/* Categories and Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="categories" className="block text-sm font-medium text-gray-700 mb-2">
                Categories (comma-separated)
              </label>
              <Input
                id="categories"
                name="categories"
                type="text"
                data-testid="edit-categories-input"
                value={formData.categories}
                onChange={handleChange}
                placeholder="Technology, Business, Finance"
              />
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                Tags (comma-separated)
              </label>
              <Input
                id="tags"
                name="tags"
                type="text"
                data-testid="edit-tags-input"
                value={formData.tags}
                onChange={handleChange}
                placeholder="innovation, startup, investment"
              />
            </div>
          </div>

          {/* Author and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-2">
                Author
              </label>
              <Input
                id="author"
                name="author"
                type="text"
                data-testid="edit-author-input"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                id="status"
                name="status"
                data-testid="edit-status-select"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center space-x-4 pt-4">
            <Button
              type="submit"
              data-testid="edit-pr-submit-button"
              disabled={saving}
              className="bg-coral-600 hover:bg-coral-700 text-white"
            >
              {saving ? 'Saving...' : (
                <span className="flex items-center space-x-2">
                  <Save size={18} />
                  <span>Save Changes</span>
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="edit-pr-cancel-button"
              onClick={() => navigate('/admin/dashboard')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPressRelease;